import { AnyFieldMeta, AnyFormState, Updater } from '@tanstack/form-core'
import { hashKey } from '@tanstack/react-query'
import { Dayjs } from 'dayjs'

import { AstraInputProps, day, diff, getOnlyDate, getToday, isAfter, isSameOrBefore } from '~/astra'

export const formatToIsoString = (date?: Dayjs) => (date && day(date).isValid() ? getOnlyDate(date) : undefined)

/**
 * Utility to check if a value has changed. Using tanstacks hashKey that deterministically generates a hash of the value.
 */
export const hasDifference = <T>(newValue: T, existingValue: T): boolean => hashKey([newValue]) !== hashKey([existingValue])

/**
 * Utility function to get a list of added, removed, and updated items between two lists of items by a given iteratee.
 *
 * Iteratee is used to generate a key for each unique item, and can thus be used to group items that should be checked for differences.
 * Uses tanstack hashkey to generate a hash of the value.
 *
 * @param newList - The new list of items
 * @param initialList - The initial list of items
 * @param iteratee - A function to generate a key for each unique item - items with the same keys are checked for differences.
 * @returns The added, removed, and updated items
 */
export function getListDifferencesBy<T>(
    newList: T[],
    initialList: T[],
    iteratee: (item: T, index: number) => unknown
): { added: T[]; removed: T[]; updated: { new: T; initial: T }[] } {
    const newListMap = new Map(newList.map((item, i) => [iteratee(item, i), item]))
    const initialListMap = new Map(initialList.map((item, i) => [iteratee(item, i), item]))
    const added = newList.filter((item, i) => iteratee(item, i) === undefined || !initialListMap.has(iteratee(item, i)))
    const removed = initialList.filter((item, i) => !newListMap.has(iteratee(item, i)))

    const updated = newList.flatMap((item, i) => {
        const initialItem = initialListMap.get(iteratee(item, i))
        const newItem = newListMap.get(iteratee(item, i))
        if (!initialItem || !newItem) return []
        if (hasDifference(item, initialItem))
            return [
                {
                    new: item,
                    initial: initialItem,
                },
            ]
        return []
    })
    return { added, removed, updated }
}

/**
 * Returns a diff of properties that are primitives (not objects).
 * @param newObject
 * @param initialObject
 * @returns
 */
export function getPrimitiveObjectDiff<T extends object>(newObject: T, initialObject: T): Partial<T> | null {
    const diff: Partial<T> = {}
    Object.keys(newObject).forEach(key => {
        const k = key as keyof T
        if (typeof newObject[k] === 'object' && typeof initialObject[k] === 'object') {
            return
        }
        if (newObject[k] !== initialObject[k]) {
            diff[k] = newObject[k]
        }
    })
    if (Object.keys(diff).length === 0) {
        return null
    }
    return diff
}

type FieldStateLike = {
    meta: Omit<AnyFieldMeta, 'errorMap'>
}
interface FieldApiLike<TData = unknown> {
    state: FieldStateLike
    handleBlur: () => void
    handleChange: (updater: Updater<TData>) => void
}

type FormFieldProps<TData> = Pick<AstraInputProps, 'error' | 'errorText'> & {
    onChange: (value: Updater<TData>) => void
    onBlur: () => void
}

type GetFormFieldPropsOptions = {
    blurOnChange?: boolean
}

type FormFieldMetaPropsOptions = {
    /**
     * If true, the error will be shown even if the field has not been touched.
     * Normally we only want to show errors if the field has been touched.
     * Can be useful if the validation of the field is dependent on other fields.
     */
    errorIgnoreTouched?: boolean
}
export function createFormFieldMetaProps<TData>(fieldApi: FieldApiLike<TData>, options: FormFieldMetaPropsOptions = {}) {
    const isError = !fieldApi.state.meta.isValid
    const showError = options.errorIgnoreTouched ? isError : fieldApi.state.meta.isTouched && isError
    const firstError = fieldApi.state.meta.errors[0]?.message
    return {
        error: showError,
        errorText: showError ? firstError : undefined,
    }
}
export function createFormFieldProps<TData>(
    fieldApi: FieldApiLike<TData>,
    { blurOnChange = false, ...metaOptions }: GetFormFieldPropsOptions & FormFieldMetaPropsOptions = {}
): FormFieldProps<TData> {
    const onChange = blurOnChange
        ? (value: Updater<TData>) => {
              fieldApi.handleChange(value)
              fieldApi.handleBlur()
          }
        : fieldApi.handleChange
    return {
        ...createFormFieldMetaProps(fieldApi, metaOptions),
        onBlur: fieldApi.handleBlur,
        onChange: onChange,
    }
}

type ProfileLike = {
    activeFrom?: Dayjs | null
}

/*
 * Sorts profiles by activeFrom, ascending - with base profile (undefined activeFrom) at the start.
 */
export const activeFromSorter = <T extends ProfileLike>(a: T, b: T) => {
    if (!a.activeFrom && !b.activeFrom) return 0
    if (!a.activeFrom) return -1
    if (!b.activeFrom) return 1
    return diff(a.activeFrom, b.activeFrom)
}

/*
 * Gets the active and future profiles from a list of profiles.
 * The active profile is the latest profile where activeFrom is before or equal to the reference date.
 * The future profiles are all profiles where activeFrom is after the reference date.
 */
export const getActiveAndFutureProfiles = <T extends ProfileLike>(profiles: T[], referenceDate: Dayjs = getToday()): { active: T | undefined; future: T[] } => {
    const allSortedByActiveFrom = profiles.toSorted(activeFromSorter)
    // Profiles are sorted, so the active profile is the "last" one where activeFrom is before or equal to the reference date.
    const active = allSortedByActiveFrom.findLast(p => !p.activeFrom || isSameOrBefore(p.activeFrom, referenceDate))
    const future = allSortedByActiveFrom.filter(p => !!p.activeFrom && isAfter(p.activeFrom, referenceDate))
    return { active, future }
}

export const parseIntOrZero = (value: string) => {
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? 0 : parsed
}

export const parseNumberList = (value: string): number[] =>
    value
        .split(',')
        .map(part => parseInt(part.trim(), 10))
        .filter(n => !Number.isNaN(n))

export const formatNumberList = (value: number[] | undefined | null) => (value ?? []).join(', ')

export const countEditedFields = (state: AnyFormState) => Object.values(state.fieldMeta).reduce((acc, curr) => (curr?.isDirty ? acc + 1 : acc), 0)
